import React from "react";
import { Activity, ActivityStatusMeta, MeetupsTableItemType } from "../../../types";
import DefaultButton from "../../default-button/DefaultButton";
import Delete from "../../../assets/icons/Delete";
import arrow from "../../../assets/svg/arrow-right-blue.svg";
import arrowLeft from "../../../assets/svg/arrow-left-white.svg";
import tick from "../../../assets/svg/tick-green.svg";
import timer from "../../../assets/svg/timer-gray.svg";
import cross from "../../../assets/svg/cross-red.svg";
import megaphone from '../../../assets/svg/megaphone.svg'
import message from "../../../assets/svg/message-gray.svg";
import { getDateOnly, showSnackbar, userHasRole } from "../../../misc/utils";
import { accessGlobalMiscState } from "../../../stores/misc";
import Tick from "../../../assets/icons/Tick";
import Cross from "../../../assets/icons/Cross";
import { simpleGetter } from "../../../actions/common";
import { activitiesApi } from "../../../api/api";
import { Megaphone } from "../../../assets/icons/Megaphone";
import { useGlobalUserState } from "../../../stores/user";

type TableItemPropsType = {
  item: Activity;
  index: number;
  selectedItem: Activity | null;
  onSelect: (activity: Activity) => void;
  onExport?: () => void;
  display: string;
  isMobile?: boolean;
};

const MeetupsTableItem = ({
  item,
  index,
  selectedItem,
  onSelect,
  display,
  onExport,
  isMobile = false
}: TableItemPropsType) => {
  const userState = useGlobalUserState();
  const user = userState.get();
  const uniqUser = user.uniqUser || {};
  const isAddFormOpen = Boolean(accessGlobalMiscState().get('isMeetupsAddFormOpen'))
  const isActive = selectedItem?.id === item.id;
  const hasDecideRole = userHasRole('decide_on_activities')
  const isOwner = item.created_by === uniqUser.id;
  // Department (creator) can delete at any stage before admin approval (status < 1)
  const canDepartmentDelete = isOwner && (item.status ?? 0) < 1;

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    onSelect(item)
  };

  const additionalClass = isActive ? "bg-[#0068FF] text-white" : "text-black";

  const renderStatus = () => {
    const meta: ActivityStatusMeta = {
      color: '#5F6B88',
      alt: 'timer',
      image: timer,
      label: 'На утверждении',
      additionalRender: () => null
    }
    let colorClass = 'text-[#5F6B88]'
    switch (item.status) {
      case 2:
        meta.color = '#0068FF'
        colorClass = 'text-[#0068FF]'
        meta.image = megaphone
        meta.alt = 'megaphone'
        meta.label = 'Опубликовано'
        break;
      case 1:
        meta.color = '#3BBC30'
        colorClass = 'text-[#3BBC30]'
        meta.image = tick
        meta.alt = 'tick'
        meta.label = 'Одобрено'
        break;
      case -1:
        meta.color = '#F12D2D'
        colorClass = 'text-[#F12D2D]'
        meta.image = cross
        meta.alt = 'cross'
        meta.label = 'Отклонено'
        meta.additionalRender = () => (
          <img 
            src={message} 
            alt="message" 
            className="cursor-pointer hover:opacity-70 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              accessGlobalMiscState().setBatch({
                meetupsFormEditItem: item,
                isMeetupsCommentModalOpen: true
              });
            }}
          />
        )
    }
    return (
      <button className={`flex items-center gap-1 ${colorClass} text-sm md:text-[15px] font-medium`}>
        <img src={meta.image} alt={meta.alt} className="w-4 h-4" />
        <span>{meta.label}</span>
        {meta.additionalRender()}
      </button>
    )
  }

  const onDelete = async () => {
  const confirmed = window.confirm(`Удалить мероприятие "${item.name}"?`);
  if (!confirmed) return;
    const response = await simpleGetter(activitiesApi.deleteActivity, item.id);
    showSnackbar(
      response.success ? "Успешно" : "Произошла ошибка",
      response.success ? "success" : "error"
    );
    if (response.success) {
      accessGlobalMiscState().set(
        "activitiesTriggerFetch",
        (accessGlobalMiscState().get("activitiesTriggerFetch") || 0) + 1
      );
    }
  }

  const updateStatus = (status: number) => async () => {
    if (status === -1) {
      accessGlobalMiscState().setBatch({
        meetupsFormEditItem: item,
        isMeetupsRejectModalOpen: true
      })
      return;
    }
    else if (status === 1) {
      accessGlobalMiscState().setBatch({
        meetupsFormEditItem: item,
        isMeetupsApproveModalOpen: true
      })
      return;
    }

    const response = await simpleGetter(activitiesApi.createActivity, { id: item.id, status });
    showSnackbar(
      response.success ? "Успешно" : "Произошла ошибка",
      response.success ? "success" : "error"
    );
    if (response.success) {
      accessGlobalMiscState().set(
        "activitiesTriggerFetch",
        (accessGlobalMiscState().get("activitiesTriggerFetch") || 0) + 1
      );
    }
  }

  const renderButtons = (isMobileView: boolean = false) => {
    if (hasDecideRole && display === "decide") {
      return (
        <div className={`flex flex-row gap-2 items-center ${isMobileView ? 'w-full min-w-0' : 'justify-end'}`}>
          <DefaultButton
            buttonText="Одобрить"
            additionalStyle={`bg-[#3BBC30] text-white px-3 md:px-4 py-[6px] hover:bg-[#35A82A] transition-colors whitespace-nowrap ${isMobileView ? 'flex-1 min-w-0' : ''}`}
            Icon={Tick}
            onClick={updateStatus(1)}
          />
          <DefaultButton
            buttonText="Отклонить"
            additionalStyle={`bg-[#F12D2D] text-white px-3 md:px-4 py-[6px] hover:bg-[#D91C1C] transition-colors whitespace-nowrap ${isMobileView ? 'flex-1 min-w-0' : ''}`}
            Icon={Cross}
            onClick={updateStatus(-1)}
          />
        </div>
      )
    }
    return (
      <div className={`flex gap-2 items-center ${isMobileView ? 'w-full min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]' : ''}`}>
        {/* Admin list view actions */}
        {hasDecideRole && display === "list" && (
          <>
            <DefaultButton
              Icon={Delete}
              additionalStyle={`bg-red-50 text-red-500 border-red-300 px-2 hover:bg-red-100 transition-colors ${isMobileView ? 'flex-shrink-0 whitespace-nowrap' : ''}`}
              onClick={onDelete}
            />
            {item.status === 1 && (
              <DefaultButton
                Icon={Megaphone}
                additionalStyle={`bg-valley-main text-white px-2 hover:bg-[#0056CC] transition-colors ${isMobileView ? 'flex-shrink-0 whitespace-nowrap' : ''}`}
                buttonText={isMobileView ? "Опубликовать" : ""}
                onClick={updateStatus(2)}
              />
            )}
          </>
        )}
        {/* Department (creator) delete before approval */}
        {!hasDecideRole && canDepartmentDelete && (
          <DefaultButton
            Icon={Delete}
            additionalStyle={`bg-red-50 text-red-500 border-red-300 px-2 hover:bg-red-100 transition-colors ${isMobileView ? 'flex-shrink-0 whitespace-nowrap' : ''}`}
            onClick={onDelete}
          />
        )}
      </div>
    )
  }

  // Мобильная версия (карточка)
  if (isMobile) {
    return (
      <div
        key={index}
        className={`border rounded-xl p-3 sm:p-4 cursor-pointer transition-all duration-200 ${
          isActive 
            ? "bg-[#0068FF] text-white border-[#0068FF] shadow-lg shadow-blue-200/50" 
            : "bg-white hover:bg-gray-50 border-gray-200 shadow-sm hover:shadow-md"
        }`}
        onClick={() => onSelect(item)}
      >
        <div className="flex items-start gap-3">
          <img
            className={`rounded-lg w-20 h-20 sm:w-24 sm:h-24 object-cover flex-shrink-0 ${isActive ? "ring-2 ring-white/30" : ""}`}
            src={item.main_image}
            alt={item.name}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className={`font-semibold text-sm sm:text-base ${isActive ? "text-white" : "text-black"} line-clamp-2 flex-1`}>
                {item.name}
              </h3>
              <img
                className={`min-w-5 min-h-5 w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 transition-transform ${isActive ? "invert" : ""}`}
                src={isActive ? arrowLeft : arrow}
                alt=""
              />
            </div>
            {!isAddFormOpen && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs sm:text-sm">
                  <span className={`px-2 py-0.5 rounded-md ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"}`}>
                    {getDateOnly(item.start_date)}
                  </span>
                  {item.department?.title && (
                    <span className={`px-2 py-0.5 rounded-md ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"}`}>
                      {item.department.title}
                    </span>
                  )}
                  {item.category?.name && (
                    <span className={`px-2 py-0.5 rounded-md ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-700"}`}>
                      {item.category.name}
                    </span>
                  )}
                </div>
                {item.location && (
                  <div className={`text-xs sm:text-sm flex items-center gap-1 ${isActive ? "text-white/90" : "text-[#5F6B88]"}`}>
                    <span>📍</span>
                    <span className="truncate">{item.location}</span>
                  </div>
                )}
                {item.target_audience?.label && (
                  <div className={`text-xs sm:text-sm flex items-center gap-1 ${isActive ? "text-white/90" : "text-[#5F6B88]"}`}>
                    <span>👥</span>
                    <span>{item.target_audience.label}</span>
                  </div>
                )}
                {display !== "decide" && (
                  <div className="mt-2">
                    {renderStatus()}
                  </div>
                )}
                <div className={`mt-3 pt-3 -mx-3 sm:-mx-4 px-3 sm:px-4 ${isActive ? 'border-t border-white/30' : 'border-t border-gray-200'}`}>
                  {renderButtons(true)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr
      key={index}
      className={`border-b border-gray-200 text-[15px] font-medium ${additionalClass} w-full`}
    >
      <td className={`px-4 py-2 leading-5 rounded-l-lg ${additionalClass}`}>
        <img
          onClick={(e) => {
            handleImageClick(e);
          }}
          className="min-w-10 min-h-10 w-10 h-10 cursor-pointer"
          src={isActive ? arrowLeft : arrow}
          alt=""
        />
      </td>
      <td className={`px-4 py-2 w-[114px] leading-5 ${additionalClass}`}>
        <img
          className={`rounded-md w-[114px] h-20 ${isActive ? "" : "opacity-50"}`}
          src={item.main_image}
          alt=""
        />
      </td>
      <td
        className={`px-4 ${hasDecideRole ? '' : 'max-w-[150px]'} py-2 text-base leading-5 ${additionalClass} rounded-r-lg`}
      >
        {item.name}
      </td>
      {!isAddFormOpen && (
        <>
          <td className="px-4 py-2 bg-white max-w-[112px] text-black">
            {getDateOnly(item.start_date)}
          </td>
          <td className="px-4 py-2 bg-white max-w-[220px] text-[#5F6B88]">
            {item.department?.title}
          </td>
          <td className="px-4 py-2 bg-white max-w-[150px] text-[#5F6B88]">
            {item.category?.name}
          </td>
          <td className="px-4 py-2 bg-white max-w-[50px] text-[#5F6B88] truncate whitespace-nowrap overflow-hidden">
            {item.target_audience?.label || "Все"}
          </td>
          <td className="px-4 py-2 bg-white text-[#5F6B88]">
            {item.location}
          </td>
          {display !== "decide" && (
            <td className="px-4 py-2 bg-white max-w-[160px]">
              {renderStatus()}
            </td>
          )}
          <td className="px-4 py-2 bg-white">
            {renderButtons(false)}
          </td>
        </>
      )}
    </tr>
  );
};

export default MeetupsTableItem;
